/**
 * Utilitário para padronização de cards de estatísticas no módulo de fossas sépticas
 * Este arquivo centraliza todos os estilos de cards para garantir consistência visual
 */

/**
 * Estilo base para cards de estatísticas
 */
const baseCardStyle = {
  backgroundColor: 'white',
  borderRadius: '12px',
  padding: '15px',
  boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
  overflow: 'hidden'
};

/**
 * Estilo para título de card
 */
export const cardTitleStyle = {
  margin: '0 0 10px 0',
  fontSize: '16px',
  fontWeight: '500'
};

/**
 * Estilo para valor principal do card
 */
export const cardValueStyle = {
  fontSize: '28px',
  fontWeight: 'bold',
  margin: '10px 0',
  color: '#333'
};

/**
 * Estilo para texto secundário do card
 */
export const cardSubtextStyle = {
  fontSize: '14px',
  color: '#666'
};

/**
 * Estilo para linha de informação dentro do card
 */
export const cardInfoLineStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  marginBottom: '8px'
};

/**
 * Estilo para container de cards
 * @param {number} columns - Número de colunas
 * @returns {Object} - Estilo para o container
 */
export const getCardGridStyle = (columns = 4) => {
  return {
    display: 'grid',
    gridTemplateColumns: `repeat(${columns}, 1fr)`,
    gap: '15px',
    marginBottom: '25px'
  };
};

/**
 * Estilo para card neutro (cinza)
 */
export const neutralCardStyle = {
  ...baseCardStyle,
  border: '1px solid #d6d8db',
  borderLeft: '5px solid #6c757d'
};

/**
 * Estilo para card de sucesso (verde)
 */
export const successCardStyle = {
  ...baseCardStyle,
  border: '1px solid #c3e6cb',
  borderLeft: '5px solid #28a745'
};

/**
 * Estilo para card de perigo (vermelho)
 */
export const dangerCardStyle = {
  ...baseCardStyle,
  border: '1px solid #f5c6cb',
  borderLeft: '5px solid #dc3545'
};

/**
 * Estilo para card de alerta (amarelo)
 */
export const warningCardStyle = {
  ...baseCardStyle,
  border: '1px solid #ffeeba',
  borderLeft: '5px solid #ffc107'
};

/**
 * Estilo para card de informação (azul)
 */
export const infoCardStyle = {
  ...baseCardStyle,
  border: '1px solid #b8daff',
  borderLeft: '5px solid #007bff'
};

/**
 * Cores para títulos de cards
 */
export const cardTitleColors = {
  neutral: '#383d41',
  success: '#155724',
  danger: '#721c24',
  warning: '#856404',
  info: '#004085'
};

/**
 * Função para obter estilo de card com base no tipo
 * @param {string} type - Tipo de card ('neutral', 'success', 'danger', 'warning', 'info')
 * @returns {Object} - Estilo completo do card
 */
export const getCardStyle = (type) => {
  switch (type) {
    case 'success':
      return successCardStyle;
    case 'danger':
      return dangerCardStyle;
    case 'warning':
      return warningCardStyle;
    case 'info':
      return infoCardStyle;
    case 'neutral':
    default:
      return neutralCardStyle;
  }
};

/**
 * Função para obter cor de título com base no tipo
 * @param {string} type - Tipo de card ('neutral', 'success', 'danger', 'warning', 'info')
 * @returns {string} - Cor do título
 */
export const getCardTitleColor = (type) => {
  return cardTitleColors[type] || cardTitleColors.neutral;
};

export default {
  getCardStyle,
  getCardTitleColor,
  getCardGridStyle,
  cardTitleStyle,
  cardValueStyle,
  cardSubtextStyle,
  cardInfoLineStyle,
  neutralCardStyle,
  successCardStyle,
  dangerCardStyle,
  warningCardStyle,
  infoCardStyle
};